import {
  LLMModel,
  LLMModelApiPriceInput,
  LLMModelFormValues,
  LLMModelUnitPriceInput,
  LLMModelUnitPriceType,
} from '../../../types/admin';
import { formatAdminNumber } from '../../../utils/numberFormat';

export const UNIT_PRICE_OPTIONS: Array<{
  unitType: LLMModelUnitPriceType;
  metric: string;
  label: string;
  shortLabel: string;
}> = [
  { unitType: 'audio_minute', metric: 'audio', label: 'USD / audio minute', shortLabel: 'min' },
  {
    unitType: 'character_1k',
    metric: 'character',
    label: 'USD / 1,000 characters',
    shortLabel: '1k chars',
  },
  { unitType: 'image', metric: 'image', label: 'USD / image', shortLabel: 'image' },
  { unitType: 'megapixel', metric: 'image', label: 'USD / megapixel', shortLabel: 'MP' },
  { unitType: 'image_step', metric: 'image', label: 'USD / image step', shortLabel: 'step' },
  {
    unitType: 'image_tile_512',
    metric: 'image',
    label: 'USD / 512px tile',
    shortLabel: 'tile',
  },
  {
    unitType: 'video_second',
    metric: 'video',
    label: 'USD / second of video',
    shortLabel: 'sec video',
  },
  { unitType: 'video', metric: 'video', label: 'USD / generated video', shortLabel: 'video' },
  { unitType: 'request', metric: 'request', label: 'USD / request', shortLabel: 'req' },
];

/** Input-field key the gateway matches against (see pricingDimensions() in aibe). */
export const RESOLUTION_DIMENSION_KEY = 'resolution';

export const RESOLUTION_UNIT_TYPES: LLMModelUnitPriceType[] = [
  'video_second',
  'video',
  'image',
  'megapixel',
];

export function isResolutionPrice(row: LLMModelUnitPriceInput): boolean {
  return row.condition?.[RESOLUTION_DIMENSION_KEY] !== undefined;
}

/** Splits rows so the resolution section and the generic unit section never edit each other's rows. */
export function splitResolutionPrices(rows: LLMModelUnitPriceInput[]): {
  resolutionRows: LLMModelUnitPriceInput[];
  otherRows: LLMModelUnitPriceInput[];
} {
  return {
    resolutionRows: rows.filter(isResolutionPrice),
    otherRows: rows.filter((row) => !isResolutionPrice(row)),
  };
}

const ADMIN_PRICE_SOURCES = new Set(['admin_catalog', 'legacy_llm_model_columns']);

const unitPriceOptionsByType = new Map(
  UNIT_PRICE_OPTIONS.map((option) => [option.unitType, option])
);

const tokenPriceFields: Array<{
  metric: string;
  priceField: keyof LLMModelFormValues;
  neuronField: keyof LLMModelFormValues;
}> = [
  {
    metric: 'input',
    priceField: 'inputPricePerMillionTokens',
    neuronField: 'inputNeuronsPerMillionTokens',
  },
  {
    metric: 'output',
    priceField: 'outputPricePerMillionTokens',
    neuronField: 'outputNeuronsPerMillionTokens',
  },
  {
    metric: 'cached_input',
    priceField: 'cachePricePerMillionTokens',
    neuronField: 'cacheNeuronsPerMillionTokens',
  },
  {
    metric: 'cache_write',
    priceField: 'cacheWritePricePerMillionTokens',
    neuronField: 'cacheWriteNeuronsPerMillionTokens',
  },
];

const SUGGESTED_UNIT_BY_TASK_TYPE: Record<string, LLMModelUnitPriceType> = {
  automatic_speech_recognition: 'audio_minute',
  text_to_speech: 'character_1k',
  text_to_image: 'image',
  image_to_image: 'image',
  text_to_video: 'video_second',
  image_to_video: 'video_second',
  video_to_text: 'video_second',
};

export function suggestedUnitPriceType(taskTypeCode?: string): LLMModelUnitPriceType | null {
  return taskTypeCode ? (SUGGESTED_UNIT_BY_TASK_TYPE[taskTypeCode] ?? null) : null;
}

export function unitPricesFromModel(model?: LLMModel | null): LLMModelUnitPriceInput[] {
  return (
    (model?.prices ?? [])
      // Only rows the backend replaces on save; other sources would be duplicated (and billed twice).
      .filter((price) => !price.source || ADMIN_PRICE_SOURCES.has(price.source))
      .filter((price) => unitPriceOptionsByType.has(price.unitType as LLMModelUnitPriceType))
      .map((price) => ({
        unitType: price.unitType as LLMModelUnitPriceType,
        priceUsd: price.priceUsd ?? null,
        unitScale: price.unitScale || 1,
        condition: normalizeCondition(price.condition),
      }))
  );
}

export function buildModelPricesPayload(
  values: LLMModelFormValues,
  hadUnitPrices: boolean
): LLMModelApiPriceInput[] | undefined {
  const unitRows = (values.unitPrices ?? []).filter(
    (row) => row.priceUsd !== null && row.priceUsd !== undefined
  );

  if (unitRows.length === 0 && !hadUnitPrices) return undefined;

  return [...buildTokenRows(values), ...buildUnitRows(unitRows)];
}

export function formatUnitPriceLabel(price: {
  unitType: string;
  unitScale?: number;
  priceUsd?: number | null;
}): string | null {
  if (price.priceUsd === null || price.priceUsd === undefined) return null;

  const option = unitPriceOptionsByType.get(price.unitType as LLMModelUnitPriceType);
  if (!option) return null;

  const unitScale = Math.max(1, Math.round(price.unitScale || 1));
  const unitLabel =
    unitScale > 1 ? `${formatAdminNumber(unitScale)} ${option.shortLabel}` : option.shortLabel;

  return `${formatAdminNumber(price.priceUsd)}$/${unitLabel}`;
}

function buildTokenRows(values: LLMModelFormValues): LLMModelApiPriceInput[] {
  return tokenPriceFields.flatMap(({ metric, priceField, neuronField }) => {
    const priceUsd = values[priceField] as number | null | undefined;
    const neurons = values[neuronField] as number | null | undefined;

    if (priceUsd === null && neurons === null) return [];
    if (priceUsd === undefined && neurons === undefined) return [];

    return [
      {
        metric,
        unitType: 'token',
        unitScale: 1_000_000,
        priceUsd: priceUsd ?? null,
        neurons: neurons ?? null,
        source: 'admin_catalog',
      },
    ];
  });
}

function buildUnitRows(unitRows: LLMModelUnitPriceInput[]): LLMModelApiPriceInput[] {
  return unitRows.flatMap((row) => {
    const option = unitPriceOptionsByType.get(row.unitType);
    if (!option) return [];

    return [
      {
        metric: option.metric,
        unitType: row.unitType,
        unitScale: Math.max(1, Math.round(row.unitScale || 1)),
        priceUsd: row.priceUsd,
        ...(normalizeCondition(row.condition)
          ? { condition: normalizeCondition(row.condition) }
          : {}),
        source: 'admin_catalog',
      },
    ];
  });
}

function normalizeCondition(
  condition?: Record<string, string | number | boolean> | null
): Record<string, string | number | boolean> | null {
  if (!condition || Object.keys(condition).length === 0) return null;
  return Object.keys(condition)
    .sort()
    .reduce<Record<string, string | number | boolean>>((result, key) => {
      result[key] = condition[key];
      return result;
    }, {});
}
